import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookmarkPlus, Loader2, Zap } from "lucide-react";
import { api } from "../lib/api";
import { PageShell, PremiumNotice } from "../components/PageShell";
import { formatApiError, useAuth } from "../context/AuthContext";
import { sharedChoices } from "./Multiverse";

const FIELDS = [["Age", "age"], ["Income & finances", "finances"], ["Career", "career"], ["Relationships", "relationship"], ["Family", "family"],
  ["Lifestyle", "lifestyle"], ["Fulfillment", "emotional_perspective"], ["Challenges", "challenges"], ["Achievements", "accomplishments"]];
const show = (v) => (Array.isArray(v) ? v.join(" · ") : v);

export default function Collide() {
  const { id } = useParams();
  const { user, checked } = useAuth();
  const [selves, setSelves] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [tab, setTab] = useState("mirror");
  const [q, setQ] = useState("");
  const [answers, setAnswers] = useState(null);
  const [turns, setTurns] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/journey/${id}/selves`).then(({ data }) => { setSelves(data); setA(data[0]?.id || ""); setB(data[1]?.id || ""); });
  }, [id]);

  const A = selves.find((s) => s.id === a);
  const B = selves.find((s) => s.id === b);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const askBoth = () => q.trim() && run(async () => setAnswers((await api.post("/collide", { self_a: a, self_b: b, mode: "ask", question: q })).data));
  const converse = (extra) => run(async () => {
    const history = extra ? [...turns, extra] : turns;
    const { data } = await api.post("/collide", { self_a: a, self_b: b, mode: "converse", history: history.map((t) => ({ speaker: t.speaker, text: t.text })) });
    setTurns([...history, ...data.turns]);
  });
  const join = () => { if (!msg.trim()) return; converse({ speaker: "You", text: msg }); setMsg(""); };
  const save = () => run(async () => {
    await api.post(`/journey/${id}/journal`, { type: "comparison", title: `${A.timeline_name} × ${B.timeline_name}`, timeline: `${A.timeline_name} / ${B.timeline_name}`, content: { answers, turns } });
    toast.success("Interaction saved to your journal.");
  });

  const pick = (val, set, testId) => (
    <select value={val} onChange={(e) => { set(e.target.value); setTurns([]); setAnswers(null); }} data-testid={testId}
      className="bg-[#0A051A] border border-white/15 rounded-full px-4 py-2 text-sm text-white">
      {selves.map((s) => <option key={s.id} value={s.id}>{s.timeline_name} (+{s.horizon}y)</option>)}
    </select>
  );
  const name = (sp) => (sp === "A" ? A?.timeline_name : sp === "B" ? B?.timeline_name : "You");

  return (
    <PageShell back={`/journey/${id}`} label="Quantum Mirror · Timeline Collision" wide title={<>Collide <span className="text-glow-purple">timelines</span></>}
      subtitle="Place two of your fictional alternate selves face to face. Compare their lives, ask them the same question, or let them talk to each other, and join in.">
      {checked && !user?.is_premium ? <PremiumNotice feature="Quantum Mirror & Timeline Collision" /> : selves.length < 2 ? (
        <div className="glass rounded-3xl p-8 text-center text-white/70" data-testid="collide-need-two">Generate at least two alternate selves on your multiverse map first.</div>
      ) : (<>
        <div className="flex flex-wrap gap-3 items-center mb-6">{pick(a, setA, "collide-select-a")}<Zap className="text-amber-300" size={18} />{pick(b, setB, "collide-select-b")}
          {A && B && <span className="text-[11px] text-amber-200/80" data-testid="collide-shared">Shared decisions: {sharedChoices(A, B).join(", ") || "none"}</span>}</div>
        <div className="flex gap-2 mb-6">{[["mirror", "Quantum Mirror"], ["collide", "Collision conversation"]].map(([k, l]) =>
          <button key={k} onClick={() => setTab(k)} className={`btn-pill text-xs ${tab === k ? "btn-pill-primary" : ""}`} data-testid={`collide-tab-${k}`}>{l}</button>)}
          <button onClick={save} disabled={busy || (!answers && !turns.length)} className="btn-pill text-xs ml-auto" data-testid="collide-save"><BookmarkPlus size={12} /> Save</button></div>

        {tab === "mirror" && A && B && (<>
          <div className="grid grid-cols-2 gap-3 md:gap-5 mb-6" data-testid="mirror-compare">
            {[A, B].map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, x: i ? 30 : -30 }} animate={{ opacity: 1, x: 0 }} className="glass rounded-3xl p-4 md:p-6" style={{ borderColor: i ? "#B026FF66" : "#00F0FF66" }}>
                <div className="font-mono-label mb-1" style={{ color: i ? "#d9a6ff" : "#00F0FF" }}>{i ? "Different choice" : "Original path"}</div>
                <h3 className="text-xl md:text-2xl font-display mb-4">{s.timeline_name}</h3>
                {FIELDS.map(([l, k]) => <div key={k} className="mb-2.5"><div className="font-mono-label">{l}</div><div className="text-xs md:text-sm text-white/75">{show(s[k])}</div></div>)}
              </motion.div>))}
          </div>
          <div className="glass rounded-3xl p-5">
            <div className="flex gap-2"><input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askBoth()} placeholder="Ask both versions the same question…" data-testid="mirror-question"
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder-white/30 focus:outline-none" />
              <button onClick={askBoth} disabled={busy} className="btn-pill btn-pill-gold" data-testid="mirror-ask">{busy ? <Loader2 size={14} className="animate-spin" /> : "Ask both"}</button></div>
            {answers && <div className="grid md:grid-cols-2 gap-3 mt-4" data-testid="mirror-answers">{[["A", answers.a], ["B", answers.b]].map(([sp, t]) =>
              <div key={sp} className="rounded-2xl border border-white/10 p-4 text-sm"><div className="text-xs text-amber-200 mb-1">{name(sp)}</div>{t}</div>)}</div>}
          </div>
        </>)}

        {tab === "collide" && (
          <div className="glass rounded-3xl p-5" data-testid="collision-chat">
            <div className="space-y-3 max-h-[480px] overflow-y-auto mb-4">
              {!turns.length && <p className="text-sm text-white/50">Two timelines are about to meet. Start the conversation.</p>}
              {turns.map((t, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: t.speaker === "B" ? 20 : -20 }} animate={{ opacity: 1, x: 0 }}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${t.speaker === "B" ? "ml-auto border border-purple-400/30 bg-purple-400/[0.05]" : t.speaker === "You" ? "mx-auto bg-cyan-400/10 border border-cyan-400/30" : "border border-cyan-300/30"}`}>
                  <div className="text-[11px] mb-1 text-white/50">{name(t.speaker)}</div>{t.text}</motion.div>))}
              {busy && <div className="text-xs text-white/50 flex gap-2 items-center"><Loader2 size={12} className="animate-spin" /> The timelines are speaking…</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => converse()} disabled={busy} className="btn-pill btn-pill-primary text-xs" data-testid="collision-continue">{turns.length ? "Continue" : "Collide timelines"}</button>
              <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && join()} placeholder="Join the conversation…" data-testid="collision-input"
                className="flex-1 min-w-[200px] bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
              <button onClick={join} disabled={busy} className="btn-pill text-xs" data-testid="collision-join">Speak</button>
            </div>
          </div>
        )}
      </>)}
    </PageShell>
  );
}
